import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { GridColDef } from '@mui/x-data-grid/models';
import { Box, IconButton, Tooltip, Button, Badge } from '@mui/material';
import { Add, Search, Refresh, GetApp } from '@mui/icons-material';
import { ServiceRequestResponse, SearchServiceRequestsRequest, ACLAuthorizationRequest } from '@/api/bol';
import { parseJsonWithDates } from '@/utils/jsonUtils';
import { 
  PageLayout, 
  DataGridContainer, 
  JsonDetailPanel,
  SearchPanel,
  createServiceRequestColumns,
  defaultServiceRequestColumnVisibilityModel,
  ServiceRequestFormDialog,
  ServiceRequestDeleteDialog,
  GenericCSVExportDialog,
} from '@/components';
import { 
  useDataGridSelection, 
  usePagination, 
  useSnackbarNotification,
  useServiceRequests,
  useStableRowId,
  usePermissions,
  useLogisticsObjectSearchUrl,
  getActionTooltip
} from '@/hooks';
import { exportToCSV, generateCSVFilename } from '@/utils/csvExport';
import { DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY } from '@/utils/searchDefaults';
import { getDefaultSavedQuery } from '@/utils/savedQueriesStorage';

const ServiceRequestsPage: React.FC = () => {
  const { can } = usePermissions();
  const {
    getSearchQueryFromUrl,
    updateSearchQueryInUrl,
    copyShareableSearchLink,
  } = useLogisticsObjectSearchUrl({ queryParamName: 'search' });
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const initialSearchQuery = useMemo<SearchServiceRequestsRequest>(
    () => {
      const searchQueryFromUrl = getSearchQueryFromUrl() as SearchServiceRequestsRequest | undefined;

      return searchQueryFromUrl
        || getDefaultSavedQuery('service-request')?.query as SearchServiceRequestsRequest
        || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    },
    [getSearchQueryFromUrl],
  );
  const [currentSearchQuery, setCurrentSearchQuery] = useState<SearchServiceRequestsRequest>(initialSearchQuery);

  const hasAppliedQueryOrSort = useMemo(() => {
    const hasQueryContent = !!currentSearchQuery?.query
      && typeof currentSearchQuery.query === 'object'
      && Object.keys(currentSearchQuery.query).length > 0;
    const hasSortContent = Array.isArray(currentSearchQuery?.sort)
      && currentSearchQuery.sort.length > 0;

    return hasQueryContent || hasSortContent;
  }, [currentSearchQuery]);
  
  const displayColumnsRef = useRef<{ columns: GridColDef[]; visibilityModel: Record<string, boolean> }>({
    columns: [],
    visibilityModel: defaultServiceRequestColumnVisibilityModel,
  });

  const handleDisplayColumnsChange = useCallback((cols: GridColDef[], visibilityModel: Record<string, boolean>) => {
    displayColumnsRef.current = { columns: cols, visibilityModel };
  }, []);

  const getRowId = useStableRowId<ServiceRequestResponse>('service-request');

  // Use custom hooks for service-request management
  const {
    serviceRequests,
    loading,
    selectedServiceRequest,
    setSelectedServiceRequest,
    fetchServiceRequests,
    createServiceRequest,
    deleteServiceRequest,
    total,
  } = useServiceRequests();

  // Use custom hooks for common functionality
  const { 
    selectedItem: selectedServiceRequestForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<ServiceRequestResponse>({ 
    data: serviceRequests, 
    getItemId: (item) => item.id || '' 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination({
    onPageChange: (page, pageSize) => {
      void fetchServiceRequests(page, pageSize, currentSearchQuery);
    }
  });

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarLink,
    snackbarType,
    showSuccess,
    showError,
    closeSnackbar
  } = useSnackbarNotification();

  // Event handlers
  const handleDeleteClick = (serviceRequest: ServiceRequestResponse) => {
    setSelectedServiceRequest(serviceRequest);
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchServiceRequests(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  const handleSearch = async (searchData: unknown) => {
    const searchBody = searchData as SearchServiceRequestsRequest;
    setCurrentSearchQuery(searchBody);
    const isSearchSuccessful = await fetchServiceRequests(0, pagination.pageSize, searchBody); // Reset to first page on search

    if (isSearchSuccessful) {
      updateSearchQueryInUrl(searchBody);
    }
  };

  const handleSearchReset = async () => {
    const defaultSearchQuery = (getDefaultSavedQuery('service-request')?.query as SearchServiceRequestsRequest)
      || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    setCurrentSearchQuery(defaultSearchQuery);
    const isResetSuccessful = await fetchServiceRequests(0, pagination.pageSize, defaultSearchQuery); // Reset to first page with default search

    if (isResetSuccessful) {
      updateSearchQueryInUrl(defaultSearchQuery);
    }
  };

  const handleCopySearchLink = useCallback(async () => {
    await copyShareableSearchLink(currentSearchQuery, {
      onSuccess: showSuccess,
      onError: showError,
    });
  }, [copyShareableSearchLink, currentSearchQuery, showError, showSuccess]);

  const handleToggleSearchPanel = () => {
    setSearchPanelOpen(!searchPanelOpen);
  };

  const handleExportCSV = (separator: string) => {
    exportToCSV({
      data: serviceRequests,
      columns: displayColumnsRef.current.columns,
      columnVisibilityModel: displayColumnsRef.current.visibilityModel,
      filename: generateCSVFilename('service_requests'),
      separator,
    });
  };

  const handleCreateServiceRequest = async (data: { memberIdentifier: string; serviceRequestJson: string; aclAuthorizations?: ACLAuthorizationRequest[] }) => {
    try {
      const { memberIdentifier, serviceRequestJson, aclAuthorizations } = data;

      // Validate required fields
      if (!memberIdentifier?.trim()) {
        throw new Error('Member identifier is required');
      }
      
      if (!serviceRequestJson?.trim()) {
        throw new Error('ServiceRequest JSON is required');
      }

      // Parse and validate JSON
      let serviceRequestData;
      try {
        serviceRequestData = parseJsonWithDates(serviceRequestJson);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        throw new Error('Invalid JSON format. Please check your JSON syntax.');
      }

      // Validate parsed data structure
      if (!serviceRequestData || typeof serviceRequestData !== 'object') {
        throw new Error('ServiceRequest data must be a valid JSON object');
      }

      await createServiceRequest(serviceRequestData, memberIdentifier, aclAuthorizations);
      setCreateDialogOpen(false);
      // Refresh the list after successful creation
      fetchServiceRequests(pagination.page, pagination.pageSize, currentSearchQuery);
    } catch (error) {
      // Error is handled by the hook, but we can add additional client-side validation errors
      console.error('ServiceRequest creation validation error:', error);
      throw error; // Re-throw to let the dialog handle the error display
    }
  };

  const handleDeleteServiceRequest = async () => {
    if (!selectedServiceRequest?.id) return;

    await deleteServiceRequest(selectedServiceRequest.id);
    setSelectedServiceRequest(null);
    // Refresh the list after successful deletion
    fetchServiceRequests(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  // Create columns configuration
  const columns = createServiceRequestColumns({
    onDelete: handleDeleteClick,
    canDelete: can.deleteLogisticsObject,
    deleteTooltip: getActionTooltip('deleteLogisticsObject')
  });

  useEffect(() => {
    fetchServiceRequests(0, 10, initialSearchQuery);
  }, [fetchServiceRequests, initialSearchQuery]);

  return (
    <PageLayout 
      title="ServiceRequests Management"
      selectedItemInfo={selectedServiceRequestForDetail ? `Selected: ${selectedServiceRequestForDetail.id}` : undefined}
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationLink={snackbarLink}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      leftTitleActions={
        <>
          <Tooltip title="Refresh service_requests list">
            <IconButton
              onClick={handleRefresh}
              size="small"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title={searchPanelOpen ? "Close search panel" : "Open search panel"}>
            <IconButton
              onClick={handleToggleSearchPanel}
              color={searchPanelOpen ? "primary" : "default"}
              size="small"
            >
              <Badge color="secondary" variant="dot" invisible={!hasAppliedQueryOrSort}>
                <Search />
              </Badge>
            </IconButton>
          </Tooltip>
        </>
      }
      titleActions={
          <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            onClick={() => setExportDialogOpen(true)}
            size="medium"
          >
            Export CSV
          </Button>
          <Tooltip title={!can.createLogisticsObject ? getActionTooltip('createLogisticsObject') : ''}>
          <span>
          <Button
                variant="contained"
                size="medium"
                onClick={() => setCreateDialogOpen(true)}
                startIcon={<Add />}
              disabled={!can.createLogisticsObject}
              >
                Create ServiceRequest
              </Button>
          </span>
        </Tooltip>
        </Box>
      }
      leftPanel={searchPanelOpen ? (
        <SearchPanel
          title="Search ServiceRequests"
          onClose={() => setSearchPanelOpen(false)}
          onSearch={handleSearch}
          onReset={handleSearchReset}
          onCopyLink={handleCopySearchLink}
          loading={loading}
          logisticsObjectType="service-request"
          currentSearchQuery={currentSearchQuery}
        />
      ) : undefined}
      sidePanel={selectedServiceRequestForDetail ? (
        <JsonDetailPanel
          title="ServiceRequest Details"
          data={selectedServiceRequestForDetail}
          onClose={handleCloseDetailPanel}
          onCopyError={() => {}} // Error handling moved to hook
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DataGridContainer
          rows={serviceRequests}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={getRowId}
          columnVisibilityModel={defaultServiceRequestColumnVisibilityModel}
          columnOrderStorageKey="service_requests-columns"
          onDisplayColumnsChange={handleDisplayColumnsChange}
        />
      </Box>

      {/* Create ServiceRequest Dialog */}
      <ServiceRequestFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateServiceRequest}
      />

      {/* Delete Confirmation Dialog */}
      <ServiceRequestDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteServiceRequest}
        serviceRequest={selectedServiceRequest}
      />

      {/* CSV Export Dialog */}
      <GenericCSVExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onSubmit={handleExportCSV}
        title="Export ServiceRequests to CSV"
        entityName="service_requests"
        defaultFilename={generateCSVFilename('service_requests')}
        currentPageInfo={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalRows: total,
        }}
      />
    </PageLayout>
  );
};

export default ServiceRequestsPage;